import React from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Link,
  Stack, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { flowService } from '../../services/flowService';
import { deliveryService } from '../../services/deliveryService';
import { track } from '../../utils/analytics';

/**
 * "Try it on your phone", the moment the whole thing is about.
 *
 * The person who just described a form gets it in their own WhatsApp, fills it in as their own
 * customer would, and watches their answers arrive in FlowGate. They message the flow's number
 * first (a tap on a link, or a scan of the code from a laptop) because that is what WhatsApp
 * requires outside a template, and because it means we never send anything to anybody who did
 * not ask.
 *
 * A demo's flow comes from FlowGate's shared number; anybody else's from their own connected
 * number, as it stands. An unpublished flow then arrives as a draft, which only phones with a role
 * on their business can open, so the dialog says that rather than leave somebody staring at a
 * form that will not open.
 *
 * While this is open we watch that flow's submissions. The arrival is the point: the panel says so
 * the moment the first one lands, with the way through to the rest of them.
 */
const POLL_MS = 3000;
/** A freshly saved flow takes a few seconds to reach WhatsApp; wait it out rather than failing. */
const SYNC_WAIT_MS = 3000;
const SYNC_ATTEMPTS = 12;

const SendToPhoneDialog = ({ open, onClose, flow }) => {
  const { authenticatedFetch, isDemoMode } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const navigate = useNavigate();
  const envId = selectedEnvironment?.id;

  const [share, setShare] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [arrived, setArrived] = React.useState(false);

  /**
   * Asked for afresh every time it opens: an edit since the last time reverts the flow to a
   * draft on WhatsApp, and a code for a draft fetches something the recipient cannot open.
   *
   * A flow saved a second ago is still on its way to WhatsApp, so "not there yet" is waited out
   * rather than shown as a failure, pressing the button is the request, and asking somebody to
   * press it again until it works is not an answer.
   */
  React.useEffect(() => {
    if (!open || !envId || !flow?.id) return undefined;
    let cancelled = false;
    setShare(null);
    setLoading(true);
    setError(null);
    setArrived(false);

    const attempt = async (remaining) => {
      try {
        const result = await flowService.shareToPhone(envId, flow.id, authenticatedFetch);
        if (cancelled) return;
        setShare(result);
        setLoading(false);
        track('sent_to_phone', { flowId: flow.id });
      } catch (e) {
        if (cancelled) return;
        if (remaining > 0 && /on its way|still going over/i.test(e.message)) {
          setTimeout(() => attempt(remaining - 1), SYNC_WAIT_MS);
          return;
        }
        setError(e.message);
        setLoading(false);
      }
    };
    attempt(SYNC_ATTEMPTS);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, envId, flow?.id]);

  // Watch for their own answers coming back. Stops at the first one: this is about the moment
  // it happens, and the Submissions page is where the list lives.
  React.useEffect(() => {
    // A demo account cannot read submissions at all (DemoRestrictionFilter), so there is nothing
    // to poll and every request would be a 403. The caption below says so rather than sitting on
    // "waiting for your answers" for ever.
    if (isDemoMode) return undefined;
    if (!open || !share || arrived || !envId || !flow?.id) return undefined;
    let cancelled = false;
    const baseline = { count: null };
    const check = async () => {
      try {
        const result = await deliveryService.listSubmissions(
          envId, { flowId: flow.id, page: 0, size: 1 }, authenticatedFetch,
        );
        if (cancelled) return;
        const total = result.total || 0;
        if (baseline.count === null) {
          baseline.count = total;
          return;
        }
        if (total > baseline.count) {
          setArrived(true);
          track('completed_on_phone', { flowId: flow.id });
        }
      } catch {
        // The watch is a nicety; a failed poll must not take the code off the screen.
      }
    };
    check();
    const timer = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoMode, open, share, arrived, envId, flow?.id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Try it on your phone</DialogTitle>
      <DialogContent>
        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">Getting your flow ready…</Typography>
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {share && !loading && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Open WhatsApp and send the message we have written for you. The form arrives in the
              chat straight away; fill it in and your answers show up here.
              {!isDemoMode && ' Send it from a phone other than your business number: a number cannot message itself.'}
            </Typography>

            {share.draft && (
              <Alert severity="info" data-testid="send-to-phone-draft">
                This flow is not published yet, so it arrives as a draft. Only phones with a role
                on your WhatsApp business can open it. Once it is published, anyone can.
              </Alert>
            )}

            {/* What they are about to send, so nothing about this is a surprise, and so anyone
                typing it by hand on another phone knows exactly what to write. */}
            <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'grey.100', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {share.message || share.code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                to {share.phoneNumber}
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              href={share.waMeUrl}
              target="_blank"
              rel="noreferrer"
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' }, py: 1.4 }}
              onClick={() => track('sent_to_phone_opened', { flowId: flow?.id })}
            >
              Open WhatsApp
            </Button>

            <Divider>or scan with your phone</Divider>

            <Box sx={{ display: 'flex', justifyContent: 'center' }} data-testid="send-to-phone-qr">
              <Box sx={{ p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <QRCodeSVG value={share.waMeUrl} size={168} />
              </Box>
            </Box>

            {arrived ? (
              <Alert
                severity="success"
                icon={<CheckCircleIcon fontSize="inherit" />}
                data-testid="submission-arrived"
                action={(
                  <Button color="inherit" size="small" onClick={() => { onClose(); navigate('/submissions'); }}>
                    See it
                  </Button>
                )}
              >
                Your submission just arrived.
              </Alert>
            ) : (
              <Typography variant="caption" color="text.secondary" align="center">
                {isDemoMode ? (
                  <>
                    Fill it in and see how it feels. Answers are kept and delivered to your email,
                    a Google Sheet or your own tools once you{' '}
                    <Link component="button" type="button" onClick={() => { onClose(); navigate('/login?tab=register'); }}>
                      create an account
                    </Link>.
                  </>
                ) : (
                  <>
                    Waiting for your answers. They appear in{' '}
                    <Link component="button" type="button" onClick={() => { onClose(); navigate('/submissions'); }}>
                      Submissions
                    </Link>
                    {' '}as soon as you send the form.
                  </>
                )}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendToPhoneDialog;
